import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import { exportFormatSchema, propertyDateKey, type ExportFormat } from '@nbh/shared';
import { adminApi } from '../../lib/adminApi';
import { propertyTimeZone } from '../../config/property';

export interface ExportReportsModalProps {
  onClose: () => void;
}

type PresetId = 'today' | 'last2' | 'last7' | 'custom';

const PRESETS: PresetId[] = ['today', 'last2', 'last7', 'custom'];

/**
 * How many days back each preset starts, counting today as day one: "Last 2 days"
 * is today and yesterday, not today and the two before it.
 */
const PRESET_DAYS_BACK: Record<Exclude<PresetId, 'custom'>, number> = {
  today: 0,
  last2: 1,
  last7: 6,
};

interface DateRange {
  from: string;
  to: string;
}

/**
 * Ticket export (SPEC.md §5.6).
 *
 * The range is two inclusive calendar dates in property time, matching
 * `exportTicketsQuerySchema`. The server re-validates: this dialog only exists so a
 * manager never waits seconds for a download that was going to be rejected.
 */
export function ExportReportsModal({ onClose }: ExportReportsModalProps) {
  const { t } = useTranslation();
  const [preset, setPreset] = useState<PresetId>('last7');
  const [range, setRange] = useState<DateRange>(() => rangeForPreset('last7'));
  const [format, setFormat] = useState<ExportFormat>('xlsx');
  const { busy, error, run } = useTicketExport(onClose);

  // Lexicographic comparison is sound for zero-padded ISO dates, and is the same
  // check the shared schema makes.
  const valid = Boolean(range.from && range.to) && range.from <= range.to;

  function choosePreset(id: PresetId) {
    setPreset(id);
    if (id !== 'custom') setRange(rangeForPreset(id));
  }

  /** Hand-editing either date is what "custom" means — no need to pick it first. */
  function editRange(patch: Partial<DateRange>) {
    setPreset('custom');
    setRange((current) => ({ ...current, ...patch }));
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!valid || busy) return;
    void run({ format, from: range.from, to: range.to });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-3 sm:items-center">
      <form
        onSubmit={submit}
        role="dialog"
        aria-modal="true"
        aria-label={t('export.heading')}
        className="w-full max-w-md rounded-xl border border-slate-700 bg-slate-950 p-4"
      >
        <p className="flex items-start gap-2 text-sm font-semibold text-slate-100">
          <Download className="mt-0.5 size-4 shrink-0 text-brookfields-gold" aria-hidden="true" />
          {t('export.heading')}
        </p>
        <p className="mt-1 text-xs text-slate-400">{t('export.explain')}</p>

        <PresetChips selected={preset} onSelect={choosePreset} />
        <RangeFields range={range} onEdit={editRange} />
        <FormatToggle value={format} onChange={setFormat} />

        {!valid && <p className="mt-2 text-xs text-amber-400">{t('export.invalidRange')}</p>}

        {error && (
          <p role="alert" className="mt-2 text-sm text-red-400">
            {error}
          </p>
        )}

        <div className="mt-4 flex gap-2">
          <button
            type="submit"
            disabled={!valid || busy}
            className="min-h-[48px] flex-1 rounded-lg bg-brookfields-green ring-1 ring-brookfields-gold/70 text-sm font-semibold text-white disabled:bg-brookfields-green-dark disabled:text-slate-400 disabled:ring-brookfields-gold-dark"
          >
            {busy ? t('export.generating') : t('export.download')}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="min-h-[48px] rounded-lg bg-transparent px-4 text-sm text-slate-400"
          >
            {t('export.cancel')}
          </button>
        </div>
      </form>
    </div>
  );
}

/** Busy/error state around one export request. */
function useTicketExport(onClose: () => void) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(query: { format: ExportFormat; from: string; to: string }) {
    setBusy(true);
    setError(null);
    try {
      const download = await adminApi.reports.exportTickets(query);
      saveBlob(download.blob, download.filename);
      setBusy(false);
      onClose();
    } catch (caught) {
      // An export can take seconds on 3G; the reason it failed has to stay on screen.
      setError(caught instanceof Error ? caught.message : t('export.failed'));
      setBusy(false);
    }
  }

  return { busy, error, run };
}

function PresetChips({
  selected,
  onSelect,
}: {
  selected: PresetId;
  onSelect: (id: PresetId) => void;
}) {
  const { t } = useTranslation();

  return (
    <div role="group" aria-label={t('export.range')} className="mt-3 flex flex-wrap gap-1.5">
      {PRESETS.map((id) => (
        <button
          key={id}
          type="button"
          aria-pressed={selected === id}
          onClick={() => onSelect(id)}
          className={`min-h-[44px] rounded-full border px-4 text-xs ${
            selected === id
              ? 'border-brookfields-gold bg-slate-800 text-slate-100'
              : 'border-slate-700 bg-transparent text-slate-300'
          }`}
        >
          {t(`export.${id}`)}
        </button>
      ))}
    </div>
  );
}

/**
 * Both bounds stay visible even under a preset, so the manager can see which days a
 * preset actually resolved to before waiting on the download.
 */
function RangeFields({
  range,
  onEdit,
}: {
  range: DateRange;
  onEdit: (patch: Partial<DateRange>) => void;
}) {
  const { t } = useTranslation();
  const fieldClass =
    'mt-1 min-h-[48px] w-full rounded border border-slate-700 bg-slate-900 px-3 text-sm text-slate-100';

  return (
    <div className="mt-3 grid grid-cols-2 gap-2">
      <label className="text-xs text-slate-400">
        {t('export.from')}
        <input
          type="date"
          value={range.from}
          max={range.to || undefined}
          onChange={(event) => onEdit({ from: event.target.value })}
          className={fieldClass}
        />
      </label>
      <label className="text-xs text-slate-400">
        {t('export.to')}
        <input
          type="date"
          value={range.to}
          min={range.from || undefined}
          onChange={(event) => onEdit({ to: event.target.value })}
          className={fieldClass}
        />
      </label>
    </div>
  );
}

function FormatToggle({
  value,
  onChange,
}: {
  value: ExportFormat;
  onChange: (format: ExportFormat) => void;
}) {
  const { t } = useTranslation();

  return (
    <div role="group" aria-label={t('export.format')} className="mt-3 flex gap-1.5">
      {exportFormatSchema.options.map((option) => (
        <button
          key={option}
          type="button"
          aria-pressed={value === option}
          onClick={() => onChange(option)}
          className={`min-h-[44px] flex-1 rounded-lg border text-xs ${
            value === option
              ? 'border-brookfields-gold bg-slate-800 text-slate-100'
              : 'border-slate-700 bg-transparent text-slate-300'
          }`}
        >
          {t(`export.${option}`)}
        </button>
      ))}
    </div>
  );
}

/**
 * Today's calendar date at the property.
 *
 * Read from property config rather than the device: the server resolves both bounds
 * in property time, so a manager checking in from another timezone would otherwise
 * ask for their "today" and be handed the property's — off by a day, with nothing on
 * screen to say so.
 */
function todayKey(): string {
  return propertyDateKey(new Date(), propertyTimeZone);
}

/** Shifts a `YYYY-MM-DD` key by whole days. */
function shiftDays(dateKey: string, days: number): string {
  // Midday UTC, so the arithmetic cannot be knocked into an adjacent date by a
  // daylight-saving shift on either end.
  const anchor = new Date(`${dateKey}T12:00:00Z`);
  anchor.setUTCDate(anchor.getUTCDate() + days);
  return anchor.toISOString().slice(0, 10);
}

function rangeForPreset(preset: Exclude<PresetId, 'custom'>): DateRange {
  const to = todayKey();
  return { from: shiftDays(to, -PRESET_DAYS_BACK[preset]), to };
}

/**
 * Writes bytes to disk through a temporary object URL.
 *
 * The URL is revoked on the next tick rather than immediately: revoking inside the
 * same task can cancel the download that the click just started.
 */
function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
